/**
 * Unicode fraction prettifier — PLAIN TEXT ONLY path.
 *
 * Unicode plain text has exactly two real fraction options:
 *   1. Pre-baked vulgar fractions: ½ ⅓ ¼ ⅕ ⅙ ⅐ ⅛ ⅑ ⅒ ⅔ ¾ ⅖ ⅗ ⅘ ⅚ ⅜ ⅝ ⅞ ↉
 *      → Used when both sides are matching simple integers.
 *   2. U+2044 FRACTION SLASH between arbitrary text: `a+b⁄c-d`
 *      → Already the cleanest option for arbitrary fractions.
 *
 * Anything else (sup-glyph + slash + sub-glyph) looks like garbage and is NOT a real
 * fraction. We don't emit that. For real stacked rendering, use the HTML clipboard
 * variant in `htmlFraction.js` which most rich-text apps honor.
 */

const VULGAR = {
  "1/2":"½","1/3":"⅓","1/4":"¼","1/5":"⅕","1/6":"⅙","1/7":"⅐","1/8":"⅛","1/9":"⅑","1/10":"⅒",
  "2/3":"⅔","3/4":"¾","2/5":"⅖","3/5":"⅗","4/5":"⅘","5/6":"⅚","3/8":"⅜","5/8":"⅝","7/8":"⅞","0/3":"↉",
};

const isWordBreak = (c) => !c || /[\s+\-=<>,;]/.test(c);

export function prettifyUnicodeFractions(text) {
  if (!text) return "";
  const s = text;
  let out = "";
  let i = 0;
  const scanNumStart = (end) => {
    if (end <= 0) return 0;
    if (s[end - 1] === ")") {
      let depth = 1;
      for (let k = end - 2; k >= 0; k--) {
        if (s[k] === ")") depth++;
        else if (s[k] === "(") { depth--; if (depth === 0) return k; }
      }
    }
    let k = end;
    while (k > 0 && !isWordBreak(s[k - 1])) k--;
    return k;
  };
  const scanDenEnd = (start) => {
    if (start >= s.length) return s.length;
    if (s[start] === "(") {
      let depth = 1;
      for (let k = start + 1; k < s.length; k++) {
        if (s[k] === "(") depth++;
        else if (s[k] === ")") { depth--; if (depth === 0) return k + 1; }
      }
    }
    let k = start;
    while (k < s.length && !isWordBreak(s[k])) k++;
    return k;
  };
  const unwrap = (str) => {
    if (str.length >= 2 && str[0] === "(" && str[str.length - 1] === ")") {
      let depth = 0;
      for (let k = 0; k < str.length; k++) {
        if (str[k] === "(") depth++;
        else if (str[k] === ")") {
          depth--;
          if (depth === 0 && k < str.length - 1) return str;
        }
      }
      return str.slice(1, -1);
    }
    return str;
  };

  while (i < s.length) {
    const idx = s.indexOf("\u2044", i);
    if (idx === -1) { out += s.slice(i); break; }
    const nStart = scanNumStart(idx);
    const dEnd = scanDenEnd(idx + 1);
    if (nStart > i) out += s.slice(i, nStart);
    const numRaw = unwrap(s.slice(nStart, idx));
    const denRaw = unwrap(s.slice(idx + 1, dEnd));
    if (numRaw && denRaw) {
      const key = `${numRaw}/${denRaw}`;
      if (VULGAR[key]) out += VULGAR[key];
      else out += `${numRaw}\u2044${denRaw}`;
    } else {
      out += s.slice(nStart, dEnd);
    }
    i = dEnd;
  }
  return out;
}
